import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { PermissionsProvider, usePermissions } from './contexts/PermissionsContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Agenda from './pages/Agenda';
import Volunteers from './pages/Volunteers';
import VolunteerForm from './pages/VolunteerForm';
import Studies from './pages/Studies';
import Estadisticas from './pages/Estadisticas';
import StudyForm from './pages/StudyForm';
import Clients from './pages/Clients';
import Warehouse from './pages/Warehouse';
import Settings from './pages/Settings';
import Audit from './pages/Audit';
import Support from './pages/Support';
import Profile from './pages/Profile';
import Communications from './pages/Communications';
import Recruitment from './pages/Recruitment';
import PortalLogin from './pages/PortalLogin';
import PortalDashboard from './pages/PortalDashboard';
import PortalStudyDetail from './pages/PortalStudyDetail';
import PublicSurvey from './pages/PublicSurvey';
import PublicValidationStatus from './pages/PublicValidationStatus';
import '@/App.css';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 border-2 border-[#1f3e80] border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-stone-500 font-body">Loading...</span>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // Redirect portal users away from internal app
  if (user.portal_access && user.roles?.includes('cliente')) return <Navigate to="/portal" replace />;
  return <Layout>{children}</Layout>;
}

// URS-F417: la ruta "/" muestra el Dashboard si el usuario tiene permiso;
// si no lo tiene, redirige al primer modulo que si pueda ver (respeta el
// orden del sidebar). Evita que un Monitor con solo "estudios" vea el
// Dashboard general en el area central pese a que la entrada lateral esta
// oculta.
const FALLBACK_ORDER = [
  { key: 'agenda', path: '/agenda' },
  { key: 'recruitment', path: '/recruitment' },
  { key: 'volunteers', path: '/volunteers' },
  { key: 'studies', path: '/studies' },
  { key: 'clients', path: '/clients' },
  { key: 'communications', path: '/communications' },
  { key: 'warehouse', path: '/warehouse' },
  { key: 'statistics', path: '/statistics' },
  { key: 'settings', path: '/settings' },
  { key: 'audit', path: '/audit' },
];

function HomeRoute() {
  const { user, loading: authLoading } = useAuth();
  const { perms, can } = usePermissions();

  if (authLoading || (user && !perms)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
        <div className="h-8 w-8 border-2 border-[#1f3e80] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.portal_access && user.roles?.includes('cliente')) return <Navigate to="/portal" replace />;

  // Admin o usuarios con permiso explicito sobre dashboard -> ver Dashboard
  if (perms?.is_admin || can('dashboard', 'view')) {
    return <Layout><Dashboard /></Layout>;
  }
  // Redirigir al primer modulo que pueda ver.
  const target = FALLBACK_ORDER.find(m => can(m.key, 'view'));
  if (target) return <Navigate to={target.path} replace />;
  // Sin permiso a nada: llevar al perfil (siempre accesible).
  return <Navigate to="/profile" replace />;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
        <div className="h-8 w-8 border-2 border-[#1f3e80] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (user) {
    if (user.portal_access && user.roles?.includes('cliente')) return <Navigate to="/portal" replace />;
    return <Navigate to="/" replace />;
  }
  return children;
}

function PortalRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
        <div className="h-8 w-8 border-2 border-[#1f3e80] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/portal/login" replace />;
  if (!user.portal_access && !user.roles?.includes('cliente')) return <Navigate to="/" replace />;
  return children;
}

function PortalPublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
        <div className="h-8 w-8 border-2 border-[#1f3e80] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (user) {
    if (user.portal_access && user.roles?.includes('cliente')) return <Navigate to="/portal" replace />;
    return <Navigate to="/" replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/" element={<HomeRoute />} />
      <Route path="/agenda" element={<ProtectedRoute><Agenda /></ProtectedRoute>} />
      <Route path="/recruitment" element={<ProtectedRoute><Recruitment /></ProtectedRoute>} />
      <Route path="/volunteers" element={<ProtectedRoute><Volunteers /></ProtectedRoute>} />
      <Route path="/volunteers/new" element={<ProtectedRoute><VolunteerForm /></ProtectedRoute>} />
      <Route path="/volunteers/:id" element={<ProtectedRoute><VolunteerForm /></ProtectedRoute>} />
      <Route path="/studies" element={<ProtectedRoute><Studies /></ProtectedRoute>} />
      <Route path="/studies/new" element={<ProtectedRoute><StudyForm /></ProtectedRoute>} />
      <Route path="/studies/:id" element={<ProtectedRoute><StudyForm /></ProtectedRoute>} />
      <Route path="/statistics" element={<ProtectedRoute><Estadisticas /></ProtectedRoute>} />
      <Route path="/clients" element={<ProtectedRoute><Clients /></ProtectedRoute>} />
      <Route path="/communications" element={<ProtectedRoute><Communications /></ProtectedRoute>} />
      <Route path="/warehouse" element={<ProtectedRoute><Warehouse /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/audit" element={<ProtectedRoute><Audit /></ProtectedRoute>} />
      <Route path="/support" element={<ProtectedRoute><Support /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      {/* Portal routes */}
      <Route path="/portal/login" element={<PortalPublicRoute><PortalLogin /></PortalPublicRoute>} />
      <Route path="/portal" element={<PortalRoute><PortalDashboard /></PortalRoute>} />
      <Route path="/portal/study/:id" element={<PortalRoute><PortalStudyDetail /></PortalRoute>} />
      {/* Public survey (no auth) */}
      <Route path="/public/survey/:token" element={<PublicSurvey />} />
      <Route path="/public/validation/:token" element={<PublicValidationStatus />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <PermissionsProvider>
            <AppRoutes />
            <Toaster />
          </PermissionsProvider>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
